import { toast } from "react-toastify";
import awsconfig from "config/aws-config";
import {
  getUserDetails,
  getRefreshToken,
  setRefreshToken,
  setAccessToken,
  getAccessToken,
  getIdToken,
  setIdToken,
  setUserDetails,
  getUserPermissions,
  setUserPermissions,
} from "config";
import { format } from "date-fns";
import { Auth } from "aws-amplify";
import Swal from "sweetalert2";
import { genericDelete, uploadFile } from "services/services.general";
// eslint-disable-next-line import/no-extraneous-dependencies
import { ToWords } from "to-words";
import { isNumber } from "lodash";

const AWS = require("aws-sdk");

const {
  CognitoUserPool,
  CognitoUser,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession,
} = require("amazon-cognito-identity-js");

/**
 * Returns an array split into n parts
 *
 * @param {array} array
 * @param {parts} number
 */
const splitArrayToChunks = (array, parts) => {
  const result = [];

  // eslint-disable-next-line no-plusplus
  for (let i = parts; i > 0; i--) {
    result.push(array.splice(0, Math.ceil(array.length / i)));
  }

  return result;
};

const Toast = (config = {}) => {
  const type = [
    toast.TYPE.DEFAULT,
    toast.TYPE.SUCCESS,
    toast.TYPE.INFO,
    toast.TYPE.WARNING,
    toast.TYPE.ERROR,
  ][config.type || 0];

  toast.error(config.content, {
    position: config.position || "top-right",
    autoClose: config.autoClose !== undefined ? config.autoClose : 3000,
    hideProgressBar: config.hideProgressBar || false,
    closeOnClick: config.closeOnClick || true,
    pauseOnHover: config.pauseOnHover || true,
    draggable: config.draggable || true,
    progress: config.progress || undefined,
    type,
    theme: "light",
  });
};

const getTokens = (session) => {
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
};

const changeUserPassword = async (username, newPassword) => {
  const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider(
    {
      region: awsconfig.aws_cognito_region,
    }
  );

  try {
    const params = {
      UserPoolId: awsconfig.aws_user_pools_id,
      Username: username,
      Password: newPassword,
      Permanent: true,
    };

    await cognitoIdentityServiceProvider.adminSetUserPassword(params).promise();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errored", error);
  }
};

const refreshTokenExpired = () => {
  try {
    const userPool = new CognitoUserPool({
      ClientId: awsconfig.aws_user_pools_web_client_id,
      UserPoolId: awsconfig.aws_user_pools_id,
    });

    const userDetails = getUserDetails();

    const username = userDetails?.username;

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    const refreshToken = getRefreshToken();

    const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

    user.refreshSession(token, (err, session) => {
      if (err) throw err;

      if (session) {
        setAccessToken(session?.accessToken?.jwtToken);
        setRefreshToken(session?.refreshToken?.token);
        setIdToken(session?.idToken?.jwtToken);
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
};

const checkTokenExpiration = () => {
  const AccessToken = new CognitoAccessToken({ AccessToken: getAccessToken() });
  const IdToken = new CognitoIdToken({ IdToken: getIdToken() });

  const RefreshToken = new CognitoRefreshToken({
    RefreshToken: getRefreshToken(),
  });

  const sessionData = {
    IdToken,
    AccessToken,
    RefreshToken,
  };

  const cachedSession = new CognitoUserSession(sessionData);

  return cachedSession.isValid();
};

const getLocalSession = async () => {
  try {
    const session = await Auth.currentSession();

    if (session) {
      setAccessToken(session?.accessToken?.jwtToken);
      setRefreshToken(session?.refreshToken?.token);
      setIdToken(session?.idToken?.jwtToken);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);

    if (
      e === "No current user" ||
      Object.values(e)[0] === "NotAuthorizedException"
    ) {
      localStorage.clear();

      if (window.location.pathname !== "/login") {
        window.location = "/login";
      }
    }
  }
};

const isTokenExpired = () => {
  const token = getAccessToken();

  const now = Date.now().valueOf() / 1000;
  const expiry = JSON.parse(window.atob(token.split(".")[1])).exp;

  return now > expiry;
};

const removeAuthentication = () => {
  setAccessToken(null, true);
  setIdToken(null, true);
  setRefreshToken(null, true);
  setUserDetails(null, true);
  setUserPermissions(null, true);
};

/**
 * Returns an array of options formatted in label, value
 *
 * @param {key} string
 * @param {rawData} array
 */
const getFormattedOptions = (key, rawData) => {
  const newData = rawData?.map((data) => ({
    ...data,
    label: data[key],
    value: data?.id,
  }));

  return newData;
};

/**
 * Returns an array of options formatted in label, value
 *
 * @param {data} array
 */
const getRegionOptions = (data) => {
  const newData =
    data?.map((region) => ({
      ...region,
      value: region.id,
      label: `${region?.acronym} - ${region?.name}`,
    })) || [];

  return newData;
};

/**
 * Returns a date string based on the format given
 *
 * @param {dateString} string
 * @param {format} string
 */
const getFormattedDateString = (dateString, formatString) => {
  if (!dateString || dateString === null) {
    return "";
  }

  return format(new Date(dateString), formatString);
};

/**
 * Returns a date format for timepicker
 *
 * @param {timeString} string
 */
const parseTimeString = (timeString) => {
  if (!timeString) return null;
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, seconds || 0, 0);

  return date;
};

/**
 * Returns a boolean if logged in user has
 * permission to proceed requested action
 *
 * @param {action} string
 */
const hasPermission = (action) => {
  return getUserPermissions()?.includes(action);
};

/**
 * Returns a formatted number with thousand separator
 * and PHP sign
 * @param {number} string
 */
const getFormattedNumber = (number) => {
  if (number === null || number === undefined || number === "") {
    return "PHP 0";
  }

  const formattedNumber = typeof number === "string" ? Number(number) : number;

  if (isNaN(formattedNumber)) {
    return "PHP 0";
  }

  return `PHP ${formattedNumber.toLocaleString("en-US")}`;
};

/**
 * Returns true if parameter is null || empty string || white spaces
 * @param {variable} string
 */
const isVariableEmpty = (variable) => {
  return variable === "" || variable === null || /^\s*$/.test(variable);
};

/**
 * Returns a contact number in the format of 639XXXXXXXXX
 * @param {contact} string
 */
const getFormattedContactNumber = (contact) => {
  if (!contact) return contact;

  let contactNumber = contact;

  // Remove any spaces or dashes first
  if (contact && (contact.includes("-") || contact.includes(" "))) {
    contactNumber = contact.replace(/[-\s]/g, "");
  }

  // Handle +63 prefix
  if (contactNumber && contactNumber.includes("+63")) {
    contactNumber = `0${contactNumber.split("+63")[1]}`;
  }
  // Handle 63 prefix (without +)
  else if (
    contactNumber &&
    contactNumber.startsWith("63") &&
    contactNumber.length > 10
  ) {
    contactNumber = `0${contactNumber.slice(2)}`;
  }
  // Handle numbers that already start with 09 - no change needed, just continue
  else if (contactNumber && contactNumber.startsWith("09")) {
    // Already in correct format, no change needed
  }
  // Handle 10-digit numbers starting with 9
  else if (
    contactNumber &&
    contactNumber.length === 10 &&
    contactNumber.startsWith("9")
  ) {
    contactNumber = `0${contactNumber}`;
  }

  return contactNumber;
};

/**
 * Returns a newData object that lists the edited fields
 * based on the two params
 * @param {initialData} object
 * @param {data} object
 */
const getModifiedObject = (initialData, data) => {
  const newData = {};

  const allKeys = new Set([...Object.keys(initialData), ...Object.keys(data)]);

  allKeys.forEach((key) => {
    if (
      key === "recipient" &&
      initialData[key]?.fullName !== data[key]?.fullName
    ) {
      newData[key] = data[key];
    } else if (initialData[key] !== data[key]) {
      newData[key] = data[key];
    }
  });

  return newData;
};

const formatTime = (timeString) => {
  if (!timeString) return "";
  const date = new Date(`1970-01-01T${timeString}`);

  return date
    .toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
};

const formatTimeToServer = (date) => {
  if (!date) return null;

  return date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
};

// eslint-disable-next-line consistent-return
const convertNumberToPesoWords = (amount) => {
  try {
    if (!isNumber(amount)) throw Error("Amount must be a integer/float");

    const toWords = new ToWords({
      localeCode: "en-PH",
      converterOptions: {
        currency: true,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false,
      },
    });

    return toWords.convert(amount);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
};

/**
 * Shows delete alert
 * @param {title} string
 * @param {url} string
 * @param {callBack} function
 */
const showDeleteModal = ({ title, url, callBack }) => {
  Swal.fire({
    title: "Warning",
    text: `Are you sure you want to delete ${title}?`,
    icon: "warning",
    confirmButtonText: "OK",
    showCancelButton: true,
    confirmButtonColor: "#00326E",
  }).then((result) => {
    if (result.isConfirmed) {
      genericDelete({
        url,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            if (callBack) callBack();

            Toast({
              type: 1,
              content: `Successfully deleted ${title}.`,
            });
          } else {
            Toast({
              type: 4,
              content: "An error occurred.",
            });
          }
        },
      });
    }
  });
};

/**
 * Upload img
 * @param {photo} file
 * @param {photoName} string
 */
const uploadImage = ({ photo, photoName }) => {
  let fileName = null;

  try {
     uploadFile({
      file: photo,
      fileName: photoName,
      onProgress: () => {
        //
      },
      callback: (data) => {
        if (data) fileName = data;
      },
    });

    if (!fileName) {
      throw new Error("Image upload returned empty filename");
    }

    return fileName;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error uploading image:", error);
    throw error;
  }
};

export {
  changeUserPassword,
  splitArrayToChunks,
  Toast,
  refreshTokenExpired,
  checkTokenExpiration,
  getLocalSession,
  isTokenExpired,
  removeAuthentication,
  getFormattedOptions,
  getFormattedDateString,
  hasPermission,
  getFormattedNumber,
  isVariableEmpty,
  getRegionOptions,
  getFormattedContactNumber,
  getModifiedObject,
  getTokens,
  showDeleteModal,
  formatTime,
  formatTimeToServer,
  parseTimeString,
  convertNumberToPesoWords,
  uploadImage,
};
